import { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../context';
import {
  ArrowLeft,
  MessageCircle,
  Phone,
  Users,
  Video,
  Wand2,
  Smile,
  Meh,
  Frown,
  ImagePlus,
  X,
} from 'lucide-react';
import { cn, timeAgo as formatTimeAgo } from '../lib/utils';
import { compressImageForUpload } from '../lib/imageUtils';
import { InteractionType } from '../types';
import ContactAvatar from './ContactAvatar';

export default function LogInteraction() {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, addInteraction } = useApp();

  const initialContactId = location.state?.contactId || '';

  const [contactId, setContactId] = useState(initialContactId);
  const [searchQuery, setSearchQuery] = useState('');
  const [type, setType] = useState<InteractionType>('text');
  const [timing, setTiming] = useState('Today');
  const [customDate, setCustomDate] = useState('');
  const [rating, setRating] = useState<'good' | 'ok' | 'bad'>('good');
  const [notes, setNotes] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const filteredContacts = state.contacts.filter((c) =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const selectedContact = state.contacts.find((c) => c.id === contactId);

  const handleAddPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const remaining = 3 - attachments.length;
    const toAdd = files.slice(0, remaining);
    const compressed = await Promise.all(
      toAdd.map((f) => compressImageForUpload(f, 1200, 1200, 0.8)),
    );
    setAttachments((prev) => [...prev, ...compressed]);
    compressed.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => setPreviews((prev) => [...prev, ev.target?.result as string]);
      reader.readAsDataURL(file);
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = (idx: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
    setPreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleLog = () => {
    if (!contactId) return;

    // Send full UTC timestamp for Today/Yesterday so the real time is preserved.
    // Custom date picker sends YYYY-MM-DD (backend anchors at noon UTC).
    let date: string;
    if (timing === 'Yesterday') {
      const d = new Date();
      d.setDate(d.getDate() - 1);
      date = d.toISOString();
    } else if (timing === 'Other' && customDate) {
      date = customDate;
    } else {
      date = new Date().toISOString();
    }

    addInteraction(
      {
        contactId,
        type,
        date,
        notes,
        rating,
      },
      attachments.length > 0 ? attachments : undefined,
    );
    navigate(-1);
  };

  const interactionTypes = [
    { id: 'text', icon: MessageCircle, label: 'Text' },
    { id: 'call', icon: Phone, label: 'Call' },
    { id: 'in_person', icon: Users, label: 'In-Person' },
    { id: 'video', icon: Video, label: 'Video' },
  ];

  const timings = ['Today', 'Yesterday', 'Other'];

  return (
    <div className="relative flex min-h-screen w-full flex-col bg-[#f8f5f5] overflow-x-hidden">
      {/* Top App Bar */}
      <header className="p-6 pb-2 flex items-center justify-between bg-[#f8f5f5] sticky top-0 z-10">
        <button
          onClick={() => navigate(-1)}
          className="size-10 rounded-full hover:bg-slate-200/50 flex items-center justify-center transition-colors -ml-2 text-slate-600"
        >
          <ArrowLeft size={24} />
        </button>
        <h2 className="text-slate-900 text-lg font-bold leading-tight tracking-tight flex-1 text-center">
          Log Interaction
        </h2>
        <div className="size-10 -mr-2"></div>
      </header>

      {/* Contact Selector (if not pre-selected) */}
      {!initialContactId && !contactId && (
        <div className="px-6 py-4 space-y-4">
          <h3 className="text-slate-900 text-sm font-bold uppercase tracking-widest">
            Who did you connect with?
          </h3>
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search contacts..."
              className="w-full rounded-xl border-slate-200 bg-slate-50 p-4 text-slate-900 focus:ring-[#fa8585] focus:border-[#fa8585] outline-none"
            />
          </div>

          {searchQuery && (
            <div className="space-y-2 max-h-48 overflow-y-auto no-scrollbar">
              {filteredContacts.map((c) => (
                <button
                  key={c.id}
                  onClick={() => {
                    setContactId(c.id);
                    setSearchQuery('');
                  }}
                  className="w-full flex items-center gap-3 p-3 bg-white border border-slate-100 rounded-xl hover:bg-slate-50 transition-colors text-left"
                >
                  <ContactAvatar photoUrl={c.photoUrl} name={c.name} size="sm" />
                  <span className="font-bold text-slate-900">{c.name}</span>
                </button>
              ))}
              {filteredContacts.length === 0 && (
                <p className="text-center text-slate-400 py-4">No contacts found</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Contact Profile Header (if selected or pre-selected) */}
      {contactId && (
        <div className="flex p-6 flex-col items-center gap-4 relative">
          {!initialContactId && (
            <button
              onClick={() => setContactId('')}
              className="absolute top-4 right-6 text-slate-400 hover:text-slate-600"
            >
              Change
            </button>
          )}
          <div className="relative">
            <div className="w-32 h-32 rounded-full border-4 border-[#fa8585]/20 overflow-hidden">
              <ContactAvatar
                photoUrl={selectedContact?.photoUrl}
                name={selectedContact?.name || ''}
                size="xl"
                className="size-full"
              />
            </div>
            <div className="absolute bottom-1 right-1 bg-green-500 w-6 h-6 rounded-full border-4 border-white"></div>
          </div>
          <div className="text-center">
            <p className="text-slate-900 text-2xl font-bold">{selectedContact?.name}</p>
            <p className="text-[#fa8585] font-medium text-sm">
              {selectedContact?.lastInteractionDate
                ? `Last spoke ${formatTimeAgo(selectedContact.lastInteractionDate).toLowerCase()}`
                : 'No interactions yet'}
            </p>
          </div>
        </div>
      )}

      {/* Interaction Types */}
      <div className="px-6">
        <h3 className="text-slate-900 text-sm font-bold uppercase tracking-widest pb-4">
          How did you connect?
        </h3>
        <div className="grid grid-cols-2 gap-3">
          {interactionTypes.map((item) => (
            <button
              key={item.id}
              onClick={() => setType(item.id as InteractionType)}
              className={cn(
                'flex flex-col gap-2 rounded-xl p-4 items-center transition-all border-2',
                type === item.id
                  ? 'border-[#fa8585] bg-[#fa8585]/5'
                  : 'border-slate-200 bg-white hover:border-[#fa8585]/50',
              )}
            >
              <item.icon
                className={cn('text-3xl', type === item.id ? 'text-[#fa8585]' : 'text-slate-600')}
                size={32}
              />
              <span className="text-slate-900 text-sm font-bold">{item.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Timing Selector */}
      <div className="px-6 py-8">
        <h3 className="text-slate-900 text-sm font-bold uppercase tracking-widest pb-4">
          When was it?
        </h3>
        <div className="flex gap-2">
          {timings.map((t) => (
            <button
              key={t}
              onClick={() => setTiming(t)}
              className={cn(
                'flex-1 py-3 px-2 rounded-lg text-xs font-bold border-2 transition-all',
                timing === t
                  ? 'border-[#fa8585] bg-[#fa8585]/5 text-[#fa8585]'
                  : 'bg-slate-100 border-transparent text-slate-500 hover:border-[#fa8585]/30',
              )}
            >
              {t}
            </button>
          ))}
        </div>
        {timing === 'Other' && (
          <div className="mt-4">
            <input
              type="date"
              value={customDate}
              onChange={(e) => setCustomDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 p-4 text-slate-900 focus:ring-2 focus:ring-[#fa8585] focus:border-[#fa8585] outline-none transition-all"
            />
          </div>
        )}
      </div>

      {/* Rating Selector */}
      <div className="px-6 pb-8">
        <h3 className="text-slate-900 text-sm font-bold uppercase tracking-widest pb-4">
          How did it go?
        </h3>
        <div className="flex gap-3">
          {[
            { id: 'good', icon: Smile, label: 'Good' },
            { id: 'ok', icon: Meh, label: 'Ok' },
            { id: 'bad', icon: Frown, label: 'Bad' },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => setRating(item.id as 'good' | 'ok' | 'bad')}
              className={cn(
                'flex flex-1 flex-col gap-2 rounded-xl p-3 items-center transition-all border-2',
                rating === item.id
                  ? 'border-[#fa8585] bg-[#fa8585]/5'
                  : 'border-slate-200 bg-white hover:border-[#fa8585]/50',
              )}
            >
              <item.icon
                className={cn('text-3xl', rating === item.id ? 'text-[#fa8585]' : 'text-slate-600')}
                size={28}
              />
              <span className="text-slate-900 text-xs font-bold">{item.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Optional Notes */}
      <div className="px-6 pb-8">
        <h3 className="text-slate-900 text-sm font-bold uppercase tracking-widest pb-4">
          What did you talk about?{' '}
          <span className="text-slate-400 font-normal lowercase">(optional)</span>
        </h3>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full rounded-xl border-slate-200 bg-slate-50 focus:ring-[#fa8585] focus:border-[#fa8585] p-4 text-slate-900 placeholder-slate-400"
          placeholder="Caught up on work and planned coffee next week..."
          rows={3}
        />
      </div>

      {/* Photo */}
      <div className="px-6 pb-8">
        <h3 className="text-slate-900 text-sm font-bold uppercase tracking-widest pb-4">
          Add a photo <span className="text-slate-400 font-normal lowercase">(optional)</span>
        </h3>

        {previews.length > 0 && (
          <div className="flex gap-2 mb-3">
            {previews.map((src, idx) => (
              <div
                key={idx}
                className="relative size-20 rounded-xl overflow-hidden border border-slate-200"
              >
                <img src={src} alt="" className="size-full object-cover" />
                <button
                  onClick={() => removeAttachment(idx)}
                  className="absolute top-1 right-1 size-5 bg-black/60 rounded-full flex items-center justify-center"
                >
                  <X size={10} className="text-white" />
                </button>
              </div>
            ))}
          </div>
        )}

        {attachments.length < 3 && (
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-full rounded-xl border-2 border-dashed border-slate-200 p-4 flex items-center justify-center gap-2 hover:border-[#fa8585] transition-colors text-sm text-slate-500"
          >
            <ImagePlus size={20} />
            <span>
              {attachments.length === 0 ? 'Add photo' : `Add more (${attachments.length}/3)`}
            </span>
          </button>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="hidden"
          onChange={handleAddPhoto}
        />
      </div>

      {/* Sticky Bottom Action */}
      <div className="mt-auto p-6 bg-[#f8f5f5] border-t border-slate-200/50">
        <button
          onClick={handleLog}
          disabled={!contactId}
          className="w-full bg-[#fa8585] hover:bg-[#fa8585]/90 disabled:opacity-50 text-white font-bold py-4 rounded-xl shadow-lg shadow-[#fa8585]/30 flex items-center justify-center gap-2 group transition-all transform active:scale-[0.98]"
        >
          <span>Log Interaction</span>
          <Wand2 size={20} className="group-hover:translate-x-1 transition-transform" />
        </button>
        <p className="text-center text-xs text-slate-400 mt-4">
          Building a better habit, one nudge at a time.
        </p>
      </div>
    </div>
  );
}
